/**
 * The team's Rules over a period, as the account's Rules page says it
 * (`GET /api/orgs/:id/rules/stats`, §5.27): read off the trail of runs, whose
 * records carry each run's Rules summary (`RunRecord.rules`) — how many runs
 * were audited and how often, what their last audits answered, how many ended
 * held by a broken enforced MUST, and which rules were found broken most.
 * Counts and rule ids only, as the trail itself keeps.
 */
import { RUN_RULES_VERDICTS, type RunRecord, type RunRulesVerdict } from './record';

/** How far back the page looks, in days. */
export const RULES_STATS_DAYS = [7, 30, 90] as const;
/** How many rules the « most broken » list names. */
export const RULES_STATS_TOP = 10;

export interface RulesStats {
  days: number;
  /** Whether these are every member's runs (an owner's or an administrator's view) or the caller's own. */
  everyone: boolean;
  /** Runs started in the period, audited or not. */
  runs: number;
  /** Runs audited at least once, and the audits asked for in all. */
  audited: number;
  audits: number;
  /** Each audited run's last verdict, counted. */
  verdicts: Record<RunRulesVerdict, number>;
  /** Runs whose last audit found an enforced MUST broken — what held their merge. */
  held: number;
  /** The rules found broken most, by how many runs broke them; `blocking` how many of those it held. */
  broken: { id: string; runs: number; blocking: number }[];
}

export function rulesStatsOf(
  records: readonly Pick<RunRecord, 'rules'>[],
  totals: { days: number; everyone: boolean; runs: number }
): RulesStats {
  const verdicts = Object.fromEntries(RUN_RULES_VERDICTS.map((one) => [one, 0])) as Record<RunRulesVerdict, number>;
  const broken = new Map<string, { runs: number; blocking: number }>();
  let audited = 0;
  let audits = 0;
  let held = 0;
  for (const record of records) {
    const rules = record.rules;
    if (!rules) continue;
    audited += 1;
    audits += rules.audits;
    verdicts[rules.verdict] += 1;
    if (rules.blocking > 0) held += 1;
    rules.violated.forEach((id, index) => {
      const was = broken.get(id) ?? { runs: 0, blocking: 0 };
      // A record lists its blocking ids first (`RunRules.violated`), so the first
      // `blocking` of them are the rules that held that run's merge.
      broken.set(id, { runs: was.runs + 1, blocking: was.blocking + (rules.blocking > 0 && index < rules.blocking ? 1 : 0) });
    });
  }
  return {
    days: totals.days,
    everyone: totals.everyone,
    runs: Math.max(totals.runs, audited),
    audited,
    audits,
    verdicts,
    held,
    broken: [...broken.entries()]
      .map(([id, one]) => ({ id, ...one }))
      .sort((a, b) => b.runs - a.runs || a.id.localeCompare(b.id))
      .slice(0, RULES_STATS_TOP)
  };
}
